import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef(null);

  // Luk dropdowns ved ruteskift
  useEffect(() => {
    setIsOpen(false);
    setIsDropdownOpen(false);
    setIsMobileServicesOpen(false);
  }, [location]);

  // Luk dropdown på desktop, hvis man klikker udenfor
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const services = [
    { name: 'Kiropraktik til heste', path: '/kiropraktik' },
    { name: 'Kiropraktik til hunde', path: '/kiropraktik-til-hunde' },
  ];

  const isServiceActive = services.some(s => location.pathname === s.path);

  const linkClass = (path) => {
    const isActive = location.pathname === path;
    return `relative py-2 text-sm font-medium tracking-wide transition-colors duration-200 ${
      isActive 
        ? 'text-brand-dark font-semibold' 
        : 'text-brand-brown/80 hover:text-brand-dark'
    }`;
  };

  const activeIndicator = (path) => {
    if (location.pathname === path) {
      return <span className="absolute bottom-0 left-0 right-0 h-[1.5px] bg-brand-dark rounded-full" />;
    }
    return null;
  };

  return (
    <header className="sticky top-0 z-40 w-full bg-brand-cream/95 backdrop-blur-md border-b border-brand-sand/40">
      <div className="mx-auto max-w-7xl px-6 h-24 md:h-28 flex items-center justify-between">
        
        {/* Logo */}
        <Link to="/" className="flex items-center group focus:outline-none" aria-label="EquiGramVet">
          <img 
            src="https://assets.auxodev.dk/cdn-cgi/image/width=900,quality=90,format=auto/assets/19a97e0e-8e3c-4b11-bc4c-e5bcc52ff735/equigram-logo-side-text.png" 
            alt="EquiGramVet" 
            className="h-16 sm:h-20 md:h-24 w-auto object-contain transition-transform duration-300 group-hover:scale-[1.02]"
          />
        </Link>

        {/* Desktop Menu (Skærme >= 1200px) */}
        <nav className="hidden min-[1200px]:flex items-center gap-8">
          <div className="relative">
            <Link to="/" className={linkClass('/')}>
              Forside
              {activeIndicator('/')}
            </Link>
          </div>

          {/* Dropdown for Behandlinger */}
          <div 
            className="relative" 
            ref={dropdownRef}
            onMouseEnter={() => setIsDropdownOpen(true)}
            onMouseLeave={() => setIsDropdownOpen(false)}
          >
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className={`flex items-center gap-1.5 py-2 text-sm font-medium tracking-wide transition-colors duration-200 cursor-pointer focus:outline-none ${
                isServiceActive ? 'text-brand-dark font-semibold' : 'text-brand-brown/80 hover:text-brand-dark'
              }`}
              aria-expanded={isDropdownOpen}
              aria-haspopup="true"
            >
              Behandlinger
              <i className={`fa-regular fa-chevron-down text-xs transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
              {isServiceActive && <span className="absolute bottom-0 left-0 right-0 h-[1.5px] bg-brand-dark rounded-full" />}
            </button>

            {/* Dropdown Menu */}
            {isDropdownOpen && (
              <div className="absolute left-0 top-full w-64 pt-1.5 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                <div className="rounded-brand bg-brand-cream border border-brand-sand/60 shadow-lg py-2">
                  {services.map((service) => (
                    <Link
                      key={service.path}
                      to={service.path}
                      className={`block px-5 py-3 text-sm transition-colors duration-150 ${
                        location.pathname === service.path
                          ? 'bg-brand-beige text-brand-dark font-semibold'
                          : 'text-brand-brown/90 hover:bg-brand-beige/50 hover:text-brand-dark'
                      }`}
                    >
                      {service.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="relative">
            <Link to="/priser" className={linkClass('/priser')}>
              Priser
              {activeIndicator('/priser')}
            </Link>
          </div>

          <div className="relative">
            <Link to="/om-equigramvet" className={linkClass('/om-equigramvet')}>
              Om EquiGramVet
              {activeIndicator('/om-equigramvet')}
            </Link>
          </div>

          <div className="relative">
            <Link to="/kontakt" className={linkClass('/kontakt')}>
              Kontakt
              {activeIndicator('/kontakt')}
            </Link>
          </div>

          {/* CTA Knap på Desktop */}
          <Link
            to="/kontakt"
            className="ml-4 px-5 py-2.5 bg-brand-dark text-brand-cream text-sm font-medium rounded-brand hover:bg-brand-brown transition-colors duration-200 shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-dark focus:ring-offset-2"
          >
            Book tid
          </Link>
        </nav>

        {/* Burgermenu-knap (Skærme < 1200px) */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex min-[1200px]:hidden items-center justify-center w-10 h-10 text-brand-dark hover:bg-brand-beige/50 rounded-brand transition-colors duration-200 cursor-pointer focus:outline-none"
          aria-label={isOpen ? 'Luk menu' : 'Åben menu'}
          aria-expanded={isOpen}
        >
          <i className={`fa-regular ${isOpen ? 'fa-xmark' : 'fa-bars'} text-xl`} />
        </button>
      </div>

      {/* Mobil Menu (Skærme < 1200px) */}
      {isOpen && (
        <div className="min-[1200px]:hidden fixed inset-0 w-screen h-screen bg-brand-cream z-50 flex flex-col overflow-y-auto animate-in fade-in duration-200">
          {/* Mobil Menu Top Bar */}
          <div className="h-24 md:h-28 px-6 flex items-center justify-between border-b border-brand-sand/30 shrink-0">
            {/* Logo */}
            <Link to="/" onClick={() => setIsOpen(false)} className="flex items-center focus:outline-none" aria-label="EquiGramVet">
              <img 
                src="https://assets.auxodev.dk/cdn-cgi/image/width=700,quality=90,format=auto/assets/19a97e0e-8e3c-4b11-bc4c-e5bcc52ff735/equigram-logo-side-text.png" 
                alt="EquiGramVet" 
                className="h-16 sm:h-20 w-auto object-contain"
              />
            </Link>

            {/* Luk knap */}
            <button
              onClick={() => setIsOpen(false)}
              className="flex items-center justify-center w-10 h-10 text-brand-dark hover:bg-brand-beige/50 rounded-brand transition-colors duration-200 cursor-pointer focus:outline-none"
              aria-label="Luk menu"
            >
              <i className="fa-regular fa-xmark text-xl" />
            </button>
          </div>

          {/* Mobil Menu Indhold */}
          <div className="flex-1 flex flex-col px-6 py-8">
            <nav className="flex flex-col gap-6">
              <Link
                to="/"
                className={`text-lg font-medium py-2 border-b border-brand-sand/20 ${
                  location.pathname === '/' ? 'text-brand-dark font-bold' : 'text-brand-brown'
                }`}
              >
                Forside
              </Link>
  
              {/* Mobil Dropdown Accordion */}
              <div className="flex flex-col">
                <button
                  onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
                  className={`flex items-center justify-between text-lg font-medium py-2 border-b border-brand-sand/20 cursor-pointer text-left focus:outline-none ${
                    isServiceActive ? 'text-brand-dark font-bold' : 'text-brand-brown'
                  }`}
                >
                  <span>Behandlinger</span>
                  <i className={`fa-regular fa-chevron-down text-sm transition-transform duration-200 ${isMobileServicesOpen ? 'rotate-180' : ''}`} />
                </button>
  
                {isMobileServicesOpen && (
                  <div className="flex flex-col gap-4 pl-4 mt-4 border-l border-brand-sand/60">
                    {services.map((service) => (
                      <Link
                        key={service.path}
                        to={service.path}
                        className={`text-base py-1 transition-colors duration-150 ${
                          location.pathname === service.path
                            ? 'text-brand-dark font-semibold'
                            : 'text-brand-brown/80 hover:text-brand-dark'
                        }`}
                      >
                        {service.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
  
              <Link
                to="/priser"
                className={`text-lg font-medium py-2 border-b border-brand-sand/20 ${
                  location.pathname === '/priser' ? 'text-brand-dark font-bold' : 'text-brand-brown'
                }`}
              >
                Priser
              </Link>

              <Link
                to="/om-equigramvet"
                className={`text-lg font-medium py-2 border-b border-brand-sand/20 ${
                  location.pathname === '/om-equigramvet' ? 'text-brand-dark font-bold' : 'text-brand-brown'
                }`}
              >
                Om EquiGramVet
              </Link>
  
              <Link
                to="/kontakt"
                className={`text-lg font-medium py-2 border-b border-brand-sand/20 ${
                  location.pathname === '/kontakt' ? 'text-brand-dark font-bold' : 'text-brand-brown'
                }`}
              >
                Kontakt
              </Link>
            </nav>
  
            {/* Mobil CTA */}
            <div className="mt-12 pt-8 border-t border-brand-sand/20">
              <Link
                to="/kontakt"
                className="block w-full text-center py-4 bg-brand-dark text-brand-cream font-medium rounded-brand hover:bg-brand-brown transition-colors duration-200 shadow-md"
              >
                Book tid til behandling
              </Link>
              <div className="mt-6 text-center text-xs text-brand-brown/70">
                <span className="block">Telefon: 26 99 24 15</span>
                <span className="block mt-1">Email: kontakt@equigramvet.dk</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
